import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Shield, Layers, FileText, Users, BarChart3, GraduationCap, Home as HomeIcon, Baby, Heart, Briefcase } from 'lucide-react';
import { MF_DISCLAIMER, NonMFNote } from '@/components/ComplianceNotice';

const Home = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Mutual Funds',
      description: 'Mutual fund distribution services – scheme information and comparison, SIP and lump-sum investment facilitation, and periodic portfolio reviews based on the investor\'s objectives and risk profile.',
      link: '/services/mutual-funds'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Fixed Deposits',
      description: 'Guidance on suitable fixed-deposit options based on safety, tenure, interest requirements and liquidity needs.',
      link: '/services/fixed-deposits'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Insurance',
      description: 'Support in understanding protection requirements and identifying suitable insurance solutions for financial security.',
      link: '/services/insurance'
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'Bonds',
      description: 'Access to suitable fixed-income opportunities for investors seeking income, diversification and capital stability.',
      link: '/services/bonds'
    },
    {
      icon: <Layers className="w-8 h-8" />,
      title: 'Alternative Investment Funds (AIFs)',
      description: 'Specialised alternative investment opportunities for eligible investors seeking diversification beyond traditional products.',
      link: '/appointment?service=AIF+Consultation',
      cta: 'Know More',
      isAIF: true
    },
    {
      icon: <HomeIcon className="w-8 h-8" />,
      title: 'Real Estate',
      description: 'Guidance and assistance relating to selected real-estate opportunities based on client requirements.',
      link: '/services/real-estate'
    }
  ];

  const whyChooseUs = [
    'Goal-oriented investment approach',
    'Mutual fund scheme information and comparison',
    'Long-term wealth creation opportunities',
    'We facilitate mutual fund investments and portfolio reviews',
    'Investor education and transparent communication',
    'Multiple financial products under one roof',
    'Focus on suitability rather than short-term market trends',
    'Continued support throughout the investment journey'
  ];

  const investmentProcess = [
    {
      step: 1,
      title: 'Understand Your Goals',
      description: 'Discuss financial priorities, investment horizon and expectations.'
    },
    {
      step: 2,
      title: 'Assess Your Risk Profile',
      description: 'Understand the investor\'s ability and willingness to handle market fluctuations.'
    },
    {
      step: 3,
      title: 'Shortlist Suitable Schemes',
      description: 'Share information on mutual fund categories and schemes that may suit your goals and time horizon.'
    },
    {
      step: 4,
      title: 'Implement the Plan',
      description: 'Assist with documentation and investment execution.'
    },
    {
      step: 5,
      title: 'Review Periodically',
      description: 'Facilitate periodic portfolio reviews and share relevant information when changes may be needed.'
    }
  ];

  const financialGoals = [
    { icon: <Users className="w-6 h-6" />, title: 'Retirement Planning' },
    { icon: <GraduationCap className="w-6 h-6" />, title: "Children's Education" },
    { icon: <Baby className="w-6 h-6" />, title: "Children's Marriage" },
    { icon: <HomeIcon className="w-6 h-6" />, title: 'Home Purchase' },
    { icon: <TrendingUp className="w-6 h-6" />, title: 'Wealth Creation' },
    { icon: <BarChart3 className="w-6 h-6" />, title: 'Regular Income' },
    { icon: <Shield className="w-6 h-6" />, title: 'Emergency Fund' },
    { icon: <Briefcase className="w-6 h-6" />, title: 'Legacy Planning' }
  ];

  const educationalArticles = [
    { title: 'What Is a Mutual Fund?', category: 'Basics' },
    { title: 'SIP vs Lump-Sum Investment', category: 'Investment Methods' },
    { title: 'Understanding Investment Risk', category: 'Risk Management' },
    { title: 'Equity, Debt and Hybrid Funds', category: 'Fund Types' },
    { title: 'How Compounding Helps Build Wealth', category: 'Wealth Creation' },
    { title: 'Why Portfolio Reviews Are Important', category: 'Portfolio Management' }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1506836467174-27f1042aa48c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjByZXRpcmVtZW50JTIwcGxhbm5pbmd8ZW58MHx8fHwxNzg0ODkyMjc0fDA&ixlib=rb-4.1.0&q=85"
            alt="Family financial planning"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-gradient"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-white mb-6 fade-in-up" data-testid="hero-heading">
              Invest with Clarity, Discipline and Purpose
            </h1>
            <p className="inline-block bg-white/15 backdrop-blur-sm border border-white/30 text-white text-sm font-body font-semibold px-4 py-1.5 rounded-full mb-5 fade-in-up" data-testid="hero-registration-badge">
              AMFI-registered Mutual Fund Distributor · ARN: 116347
            </p>
            <p className="text-lg sm:text-xl text-white/90 mb-8 font-body leading-relaxed fade-in-up stagger-1">
              Impetus Investment Services helps individuals and families make informed mutual fund investment decisions, facilitates goal-oriented investing and supports them on the path towards long-term financial wellness.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 fade-in-up stagger-2">
              <button
                onClick={() => navigate('/appointment')}
                className="btn-secondary"
                data-testid="hero-book-appointment-btn"
              >
                Book an Appointment
              </button>
              <button
                onClick={() => navigate('/services')}
                className="bg-white text-[#A0451E] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
                data-testid="hero-explore-services-btn"
              >
                Explore Our Services
              </button>
            </div>
            <div className="mt-6">
              <button
                onClick={() => navigate('/education')}
                className="text-white underline hover:text-white/80 transition font-body"
                data-testid="hero-learn-mutual-funds-link"
              >
                Learn About Mutual Funds →
              </button>
            </div>
            <p className="mt-6 text-xs text-white/80 font-body" data-testid="hero-mf-disclaimer">{MF_DISCLAIMER}</p>
          </div>
        </div>
      </section>

      {/* Trust and Experience */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#FDF8F5] rounded-2xl p-8 border border-[#E5E5E5] card-hover" data-testid="aum-stat-card">
              <h3 className="text-4xl font-heading font-semibold text-[#A0451E] mb-2">₹300 Crore</h3>
              <p className="text-[#4A4A4A] font-body">Client Mutual Fund Investments Serviced (Approx.)</p>
            </div>
            <div className="bg-[#FDF8F5] rounded-2xl p-8 border border-[#E5E5E5] card-hover" data-testid="clients-stat-card">
              <h3 className="text-4xl font-heading font-semibold text-[#A0451E] mb-2">500+</h3>
              <p className="text-[#4A4A4A] font-body">Clients Served</p>
            </div>
          </div>
          <p className="text-sm text-[#737373] text-center mt-6 font-body">
            Figures are based on information provided by the firm and may be updated periodically.
          </p>
        </div>
      </section>

      {/* Introductory Statement */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-medium text-[#A0451E] leading-tight" data-testid="intro-statement">
            Helping clients pursue their financial goals through disciplined investing and long-term wealth creation opportunities.
          </h2>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 section-bg-secondary" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-medium text-[#A0451E] mb-4">
              Our Services
            </h2>
            <p className="text-base font-body text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed">
              Mutual fund distribution, along with other financial products arranged through their respective providers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border border-[#E5E5E5] shadow-sm card-hover cursor-pointer flex flex-col"
                onClick={() => navigate(service.link)}
                data-testid={service.isAIF ? 'service-card-aif' : `service-card-${index}`}
              >
                <div className="text-[#D97706] mb-4">{service.icon}</div>
                <h3 className="text-xl font-heading font-medium text-[#A0451E] mb-3">
                  {service.title}
                </h3>
                <p className="text-sm font-body text-[#4A4A4A] leading-relaxed mb-4 flex-grow">
                  {service.description}
                </p>
                <button className="text-[#D97706] font-semibold text-sm hover:underline font-body text-left" data-testid={service.isAIF ? 'aif-know-more-btn' : undefined}>
                  {service.cta || 'Learn More'} →
                </button>
              </div>
            ))}
          </div>

          <p className="text-xs font-body text-[#737373] leading-relaxed mt-8 max-w-4xl italic" data-testid="aif-disclaimer-home">
            <strong className="text-[#A0451E] not-italic">Note on AIFs:</strong> AIFs are complex, high-risk and generally illiquid investment products intended for eligible investors. Investment is subject to applicable regulations, minimum investment requirements and suitability assessment.
          </p>
          <NonMFNote testId="non-mf-note-home" className="mt-3 max-w-4xl" />
        </div>
      </section>

      {/* Why Choose Impetus */}
      <section className="py-20 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-medium text-[#A0451E] mb-4">
              Why Investors Choose Impetus
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((reason, index) => (
              <div
                key={index}
                className="bg-[#FDF8F5] rounded-xl p-6 border border-[#E5E5E5] card-hover"
                data-testid={`why-choose-card-${index}`}
              >
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-[#D97706] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-sm font-body text-[#4A4A4A] leading-relaxed">{reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Process */}
      <section className="py-20 section-bg-secondary" data-testid="investment-process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-medium text-[#A0451E] mb-4">
              Our Investment Process
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {investmentProcess.map((item, index) => (
              <div key={index} className="text-center" data-testid={`process-step-${index}`}>
                <div className="bg-[#A0451E] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-heading font-semibold">
                  {item.step}
                </div>
                <h3 className="text-lg font-heading font-medium text-[#A0451E] mb-2">
                  {item.title}
                </h3>
                <p className="text-sm font-body text-[#4A4A4A] leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Goals */}
      <section className="py-20 bg-white" data-testid="financial-goals-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-medium text-[#A0451E] mb-4">
              Plan for Your Financial Goals
            </h2>
            <p className="text-base font-body text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed">
              Whatever your aspirations, we can help you explore mutual fund options that may suit them
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {financialGoals.map((goal, index) => (
              <div
                key={index}
                className="bg-[#FDF8F5] rounded-xl p-6 border border-[#E5E5E5] text-center card-hover cursor-pointer"
                onClick={() => navigate('/appointment')}
                data-testid={`goal-card-${index}`}
              >
                <div className="text-[#D97706] flex justify-center mb-3">{goal.icon}</div>
                <h3 className="text-sm font-body font-semibold text-[#A0451E]">{goal.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Educational Content Preview */}
      <section className="py-20 section-bg-secondary" data-testid="education-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-medium text-[#A0451E] mb-4">
              Learn Before You Invest
            </h2>
            <p className="text-base font-body text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed">
              Empower yourself with knowledge to make informed financial decisions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {educationalArticles.map((article, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-[#E5E5E5] card-hover cursor-pointer"
                onClick={() => navigate('/education')}
                data-testid={`education-card-${index}`}
              >
                <span className="inline-block bg-[#D97706] text-white text-xs px-3 py-1 rounded-full mb-3 font-body">
                  {article.category}
                </span>
                <h3 className="text-lg font-heading font-medium text-[#A0451E] mb-2">
                  {article.title}
                </h3>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/education')}
              className="btn-primary"
              data-testid="visit-education-centre-btn"
            >
              Visit Investor Education Centre
            </button>
          </div>
        </div>
      </section>

      {/* Appointment CTA */}
      <section className="py-20 bg-[#A0451E] text-white" data-testid="appointment-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-medium mb-6">
            Let Us Discuss Your Financial Goals
          </h2>
          <p className="text-base font-body text-white/90 mb-8 leading-relaxed">
            Schedule a conversation with our team to understand how a structured and goal-based investment approach may support your financial journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/appointment')}
              className="bg-white text-[#A0451E] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition btn-hover-lift"
              data-testid="cta-book-appointment-btn"
            >
              Book an Appointment
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="bg-[#D97706] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#B85D08] transition btn-hover-lift"
              data-testid="cta-request-callback-btn"
            >
              Request a Callback
            </button>
            <a
              href="tel:+919841790000"
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-[#A0451E] transition btn-hover-lift"
              data-testid="cta-call-us-btn"
            >
              Call Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;